import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { challengesApi } from '../lib/api';
import { Trophy, ArrowLeft, Target, Users, Clock, Flame, Zap, UserPlus, TrendingUp } from 'lucide-react';

const TYPE_CONFIG = {
  workout_count: { icon: Target, label: 'Workout Count', color: '#E63946' },
  streak: { icon: Flame, label: 'Streak', color: '#FF6B35' },
  custom_metric: { icon: Zap, label: 'Custom Goal', color: '#00F0FF' },
};

function daysLeft(endDate) {
  const diff = new Date(endDate) - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function LogProgressForm({ challengeId, challenge, token, onLogged }) {
  const [value, setValue] = useState('1');
  const [note, setNote] = useState('');
  const [logging, setLogging] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!value || Number(value) <= 0) return;
    setLogging(true);
    setResult(null);
    try {
      const res = await challengesApi.log(challengeId, { value: Number(value), note }, token);
      setResult(res.data);
      setValue('1');
      setNote('');
      onLogged();
    } catch (err) {
      console.error('Log error:', err);
    }
    setLogging(false);
  }

  return (
    <div className="bg-zinc-900/60 border border-zinc-800 p-5" data-testid="log-progress-form">
      <h3 className="text-white text-sm uppercase tracking-wider mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Log Progress</h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1">Amount</label>
            <div className="flex items-center gap-2">
              <input type="number" value={value} onChange={e => setValue(e.target.value)} min="0.1" step="any"
                className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
                data-testid="log-value-input" />
              <span className="text-zinc-500 text-xs flex-shrink-0">{challenge.unit}</span>
            </div>
          </div>
        </div>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="Quick note (optional)"
          className="w-full bg-zinc-800/50 border border-zinc-700 text-white text-sm px-3 py-2 placeholder-zinc-600 focus:outline-none focus:border-zinc-500"
          data-testid="log-note-input" maxLength={500} />
        <button type="submit" disabled={logging}
          className="w-full py-2.5 bg-[#E63946] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#E63946]/80 transition-colors"
          style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="log-submit-btn"
        >{logging ? 'Logging...' : 'Log Progress'}</button>
      </form>

      {result && (
        <div className="mt-3 p-3 border border-zinc-700 bg-zinc-800/40" data-testid="log-result">
          <div className="flex items-center gap-2 mb-2">
            {result.completed ? (
              <span className="text-[#00F0FF] text-xs uppercase tracking-wider font-bold flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5" /> Challenge Complete!
              </span>
            ) : (
              <span className="text-zinc-400 text-xs">Progress logged!</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white text-sm">{result.progress_value}/{result.target_value}</span>
            <span className="text-zinc-500 text-xs">{result.percentage}%</span>
            <span className="text-zinc-500 text-xs flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" /> {result.streak_count} day streak</span>
          </div>
          <div className="h-1.5 bg-zinc-800 mt-2 overflow-hidden">
            <div className="h-full bg-[#E63946] transition-all duration-500" style={{ width: `${result.percentage}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

function Podium({ entries, challenge }) {
  if (entries.length < 1) return null;
  const cfg = TYPE_CONFIG[challenge.type] || TYPE_CONFIG.workout_count;

  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
  const top3 = entries.slice(0, 3);

  return (
    <div className="flex items-end justify-center gap-3 mb-8 pt-4" data-testid="podium">
      {/* 2nd place */}
      {top3[1] && (
        <div className="text-center w-28">
          <Link to={`/community/${top3[1].username}`} className="block">
            <div className="w-12 h-12 mx-auto bg-zinc-800 border-2 flex items-center justify-center text-zinc-400 text-lg font-bold uppercase mb-2" style={{ borderColor: medals[1], fontFamily: 'Bebas Neue, sans-serif' }}>
              {top3[1].avatar ? <img src={top3[1].avatar} alt="" className="w-full h-full object-cover" /> : top3[1].display_name?.charAt(0) || '?'}
            </div>
          </Link>
          <div className="bg-zinc-800/60 py-3 border-t-2" style={{ borderColor: medals[1] }}>
            <span className="text-lg font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif', color: medals[1] }}>2</span>
            <Link to={`/community/${top3[1].username}`} className="text-white text-xs hover:text-[#00F0FF] transition-colors block truncate px-1">{top3[1].display_name}</Link>
            <span className="text-zinc-500 text-[10px]">{top3[1].progress_value} {challenge.unit}</span>
          </div>
        </div>
      )}

      {/* 1st place */}
      {top3[0] && (
        <div className="text-center w-32">
          <Link to={`/community/${top3[0].username}`} className="block">
            <div className="w-16 h-16 mx-auto bg-zinc-800 border-2 flex items-center justify-center text-zinc-400 text-xl font-bold uppercase mb-2" style={{ borderColor: medals[0], fontFamily: 'Bebas Neue, sans-serif' }}>
              {top3[0].avatar ? <img src={top3[0].avatar} alt="" className="w-full h-full object-cover" /> : top3[0].display_name?.charAt(0) || '?'}
            </div>
          </Link>
          <div className="bg-zinc-800/60 py-4 border-t-2" style={{ borderColor: medals[0] }}>
            <Trophy className="w-5 h-5 mx-auto mb-1" style={{ color: medals[0] }} />
            <span className="text-xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif', color: medals[0] }}>1</span>
            <Link to={`/community/${top3[0].username}`} className="text-white text-sm hover:text-[#00F0FF] transition-colors block truncate px-1">{top3[0].display_name}</Link>
            <span className="text-zinc-500 text-xs">{top3[0].progress_value} {challenge.unit}</span>
          </div>
        </div>
      )}

      {/* 3rd place */}
      {top3[2] && (
        <div className="text-center w-28">
          <Link to={`/community/${top3[2].username}`} className="block">
            <div className="w-12 h-12 mx-auto bg-zinc-800 border-2 flex items-center justify-center text-zinc-400 text-lg font-bold uppercase mb-2" style={{ borderColor: medals[2], fontFamily: 'Bebas Neue, sans-serif' }}>
              {top3[2].avatar ? <img src={top3[2].avatar} alt="" className="w-full h-full object-cover" /> : top3[2].display_name?.charAt(0) || '?'}
            </div>
          </Link>
          <div className="bg-zinc-800/60 py-3 border-t-2" style={{ borderColor: medals[2] }}>
            <span className="text-lg font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif', color: medals[2] }}>3</span>
            <Link to={`/community/${top3[2].username}`} className="text-white text-xs hover:text-[#00F0FF] transition-colors block truncate px-1">{top3[2].display_name}</Link>
            <span className="text-zinc-500 text-[10px]">{top3[2].progress_value} {challenge.unit}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ChallengeDetailPage() {
  const { challengeId } = useParams();
  const { user, token } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, lRes] = await Promise.all([
        challengesApi.get(challengeId, token),
        challengesApi.leaderboard(challengeId),
      ]);
      setChallenge(cRes.data);
      setLeaderboard(lRes.data.leaderboard || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [challengeId, token]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleJoin() {
    if (!token) return;
    setJoining(true);
    try {
      await challengesApi.join(challengeId, token);
      loadData();
    } catch (e) { console.error(e); }
    setJoining(false);
  }

  if (loading) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="container-ninja section-spacing max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-zinc-800 w-1/3" /><div className="h-48 bg-zinc-800" /><div className="h-64 bg-zinc-800" />
          </div>
        </div>
      </main>
    );
  }

  if (!challenge) {
    return (
      <main className="pt-20 min-h-screen" data-testid="challenge-not-found">
        <div className="container-ninja section-spacing text-center">
          <h1 className="text-white text-3xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Challenge Not Found</h1>
          <Link to="/community/challenges" className="btn-ninja inline-flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back to Challenges</Link>
        </div>
      </main>
    );
  }

  const cfg = TYPE_CONFIG[challenge.type] || TYPE_CONFIG.workout_count;
  const Icon = cfg.icon;
  const remaining = daysLeft(challenge.end_date);
  const myEntry = challenge.my_entry;
  const myPct = myEntry ? Math.min(100, Math.round((myEntry.progress_value / challenge.target_value) * 100)) : 0;

  return (
    <main className="pt-20 min-h-screen" data-testid="challenge-detail-page">
      <div className="container-ninja section-spacing max-w-4xl mx-auto">
        {/* Back */}
        <Link to="/community/challenges" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8" data-testid="back-to-challenges">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Challenges</span>
        </Link>

        {/* Challenge Header */}
        <div className="bg-zinc-900/60 border border-zinc-800 mb-6 overflow-hidden" data-testid="challenge-header">
          <div className="h-1.5" style={{ background: cfg.color }} />
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center border border-zinc-700" style={{ color: cfg.color, borderColor: `${cfg.color}30` }}>
                <Icon className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <span className="text-[10px] uppercase tracking-wider mb-1 block" style={{ color: cfg.color }}>{cfg.label}</span>
                <h1 className="text-white text-2xl sm:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="challenge-detail-title">
                  {challenge.title}
                </h1>
              </div>
            </div>

            {challenge.description && (
              <p className="text-zinc-400 text-sm mb-5 leading-relaxed">{challenge.description}</p>
            )}

            {/* Stats row */}
            <div className="flex flex-wrap gap-6 mb-5">
              <div className="text-center">
                <span className="text-white text-xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{challenge.target_value}</span>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{challenge.unit} goal</span>
              </div>
              <div className="text-center">
                <span className="text-white text-xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{challenge.participants_count}</span>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Participants</span>
              </div>
              <div className="text-center">
                <span className={`text-xl font-bold block ${remaining <= 3 ? 'text-[#E63946]' : 'text-white'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{remaining}</span>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Days Left</span>
              </div>
              <div className="text-center">
                <span className="text-zinc-400 text-xs">by <Link to={`/community/${challenge.creator_username}`} className="text-[#00F0FF] hover:underline">{challenge.creator_display_name}</Link></span>
              </div>
            </div>

            {/* My progress if joined */}
            {myEntry && (
              <div className="bg-zinc-800/40 border border-zinc-700 p-4 mb-4" data-testid="my-progress-section">
                <div className="flex justify-between text-xs uppercase tracking-wider mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  <span className="text-zinc-400">Your Progress</span>
                  <span style={{ color: cfg.color }}>{myEntry.progress_value} / {challenge.target_value} {challenge.unit} ({myPct}%)</span>
                </div>
                <div className="h-2 bg-zinc-800 overflow-hidden mb-2">
                  <div className="h-full transition-all duration-700" style={{ width: `${myPct}%`, background: cfg.color }} />
                </div>
                <div className="flex gap-4">
                  <span className="text-zinc-500 text-xs flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" /> {myEntry.streak_count} day streak</span>
                  {myEntry.logs && <span className="text-zinc-500 text-xs">{myEntry.logs.length} entries</span>}
                </div>
              </div>
            )}

            {/* Join button */}
            {!challenge.joined && token && challenge.status === 'active' && (
              <button onClick={handleJoin} disabled={joining}
                className="px-6 py-3 text-white text-xs uppercase tracking-wider flex items-center gap-2 hover:opacity-80 transition-opacity disabled:opacity-40"
                style={{ fontFamily: 'Bebas Neue, sans-serif', background: cfg.color }}
                data-testid="join-challenge-btn"
              >
                <UserPlus className="w-4 h-4" /> {joining ? 'Joining...' : 'Join This Challenge'}
              </button>
            )}
            {!token && (
              <Link to="/auth" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Sign In to Join
              </Link>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leaderboard */}
          <div className="lg:col-span-2">
            <div className="bg-zinc-900/60 border border-zinc-800 p-6" data-testid="leaderboard-section">
              <h2 className="text-white text-lg uppercase tracking-wider mb-6 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                <Trophy className="w-5 h-5 text-[#FFD700]" /> Leaderboard
              </h2>

              {leaderboard.length >= 3 && <Podium entries={leaderboard} challenge={challenge} />}

              {/* Full table */}
              <div className="space-y-1" data-testid="leaderboard-table">
                {leaderboard.length > 0 ? leaderboard.map((entry, i) => {
                  const pct = entry.percentage || 0;
                  const isMe = myEntry && entry.user_id === myEntry.user_id;

                  return (
                    <div key={entry.entry_id} className={`flex items-center gap-3 p-3 transition-colors ${isMe ? 'bg-zinc-800/60 border border-zinc-700' : 'hover:bg-zinc-800/30'}`} data-testid={`leaderboard-row-${i}`}>
                      <span className={`w-8 text-center text-sm font-bold ${i < 3 ? 'text-[#FFD700]' : 'text-zinc-600'}`} style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                        {entry.rank || i + 1}
                      </span>
                      <Link to={`/community/${entry.username}`} className="flex-shrink-0">
                        <div className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 text-xs font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                          {entry.avatar ? <img src={entry.avatar} alt="" className="w-full h-full object-cover" /> : entry.display_name?.charAt(0) || '?'}
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/community/${entry.username}`} className="text-white text-sm hover:text-[#00F0FF] transition-colors block truncate">
                          {entry.display_name} {isMe && <span className="text-zinc-500 text-[10px]">(you)</span>}
                        </Link>
                        <div className="h-1 bg-zinc-800 mt-1 overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${pct}%`, background: cfg.color }} />
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-white text-sm font-medium block">{entry.progress_value}</span>
                        <span className="text-zinc-600 text-[10px]">{pct}%</span>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-1 text-zinc-500 text-xs">
                        <Flame className="w-3 h-3 text-orange-400/60" /> {entry.streak_count || 0}
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-12">
                    <Users className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">No participants yet. Be the first to join!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar: Log Progress */}
          <aside className="space-y-6">
            {challenge.joined && challenge.status === 'active' && (
              <LogProgressForm
                challengeId={challengeId}
                challenge={challenge}
                token={token}
                onLogged={loadData}
              />
            )}

            {/* Recent Logs */}
            {myEntry && myEntry.logs && myEntry.logs.length > 0 && (
              <div className="bg-zinc-900/40 border border-zinc-800 p-5" data-testid="recent-logs">
                <h3 className="text-white text-sm uppercase tracking-wider mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Log</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {[...myEntry.logs].reverse().slice(0, 10).map((log, i) => (
                    <div key={log.value} className="flex items-center gap-3 py-1.5 border-b border-zinc-800/40 last:border-0">
                      <span className="text-zinc-600 text-[10px] flex-shrink-0">{log.date}</span>
                      <span className="text-white text-xs">+{log.value} {challenge.unit}</span>
                      {log.note && <span className="text-zinc-500 text-[10px] truncate">{log.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
}
